import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Button, InputGroup } from 'react-bootstrap';
import KeisukeUnifiedChartFontControls from '../charts/KeisukeUnifiedChartFontControls';
import {
  coalesceReferenceLine,
  normalizeMarginAdjust,
  normalizeDashboardBarChartYAxisScaleType,
  type DashboardBarChartSettings,
} from './keisukeDashboardBarChartSettings';
type CategoryPlotKind = DashboardBarChartSettings['plot']['categoryPlotKind'];
import type { DashboardBarErrorBarType } from './keisukeDashboardChartUtils';
import styles from './KeisukeDashboardChart.module.scss';

interface Props {
  chartSettings: DashboardBarChartSettings;
  setChartSettings: React.Dispatch<React.SetStateAction<DashboardBarChartSettings>>;
  onReset: () => void;
  categories: string[];
  fieldIdPrefix: string;
}

type TabId = 'basic' | 'yaxis' | 'xaxis' | 'significance' | 'detail';

const TAB_IDS: TabId[] = ['basic', 'yaxis', 'xaxis', 'significance', 'detail'];

export default function KeisukeDashboardChartSettingsPanel({
  chartSettings,
  setChartSettings,
  onReset,
  categories,
  fieldIdPrefix,
}: Props) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabId>('basic');
  const fid = (suffix: string) => `${fieldIdPrefix}-${suffix}`;

  const categoryPlotKind = chartSettings.plot.categoryPlotKind ?? 'bar';

  return (
    <div className={styles.settingsPanel}>
      {/* Header */}
      <div className={styles.settingsPanelHeader}>
        <span className={styles.settingsPanelTitle}>
          <i className="fas fa-sliders-h me-1" aria-hidden />
          {t('keisuke.dashboard.chart.settings')}
        </span>
        <button
          type="button"
          className={styles.settingsPanelResetBtn}
          onClick={onReset}
          title={t('keisuke.dashboard.chart.resetToDefaultsHint')}
          aria-label={t('keisuke.dashboard.chart.resetToDefaultsHint')}
        >
          <i className="fas fa-undo-alt" aria-hidden />
        </button>
      </div>

      {/* Tab bar */}
      <div className={styles.settingsTabBar} role="tablist">
        {TAB_IDS.map((tabId) => (
          <button
            key={tabId}
            type="button"
            role="tab"
            aria-selected={activeTab === tabId}
            className={`${styles.settingsTab}${activeTab === tabId ? ` ${styles.settingsTabActive}` : ''}`}
            onClick={() => setActiveTab(tabId)}
          >
            {t(`keisuke.dashboard.chartSettings.tabs.${tabId}`)}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className={styles.settingsPanelContent} role="tabpanel">

        {/* ── 基本タブ ── */}
        {activeTab === 'basic' && (
          <div className={styles.settingsFieldGrid}>
            {/* グラフ種類 */}
            <Form.Group controlId={fid('plot-categoryKind')} className={styles.settingsFieldFull}>
              <Form.Label>{t('keisuke.dashboard.chart.categoryPlotKind')}</Form.Label>
              <Form.Select
                size="sm"
                name={fid('plot-categoryKind')}
                value={categoryPlotKind}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    plot: { ...s.plot, categoryPlotKind: e.target.value as CategoryPlotKind },
                  }))
                }
              >
                <option value="bar">{t('keisuke.dashboard.chart.categoryPlotBar')}</option>
                <option value="box">{t('keisuke.dashboard.chart.categoryPlotBox')}</option>
                <option value="line">{t('keisuke.dashboard.chart.categoryPlotLine')}</option>
                <option value="scatter">{t('keisuke.dashboard.chart.categoryPlotScatter')}</option>
              </Form.Select>
            </Form.Group>

            {/* エラーバー */}
            <Form.Group controlId={fid('plot-errorBarType')}>
              <Form.Label>{t('keisuke.dashboard.chart.errorBars')}</Form.Label>
              <Form.Select
                size="sm"
                name={fid('plot-errorBarType')}
                value={chartSettings.groupDisplay.errorBarType}
                disabled={categoryPlotKind === 'box'}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    groupDisplay: { ...s.groupDisplay, errorBarType: e.target.value as DashboardBarErrorBarType },
                  }))
                }
              >
                <option value="none">{t('keisuke.dashboard.chart.errorBarNone')}</option>
                <option value="sd">{t('keisuke.dashboard.chart.errorBarSD')}</option>
                <option value="se">{t('keisuke.dashboard.chart.errorBarSE')}</option>
                <option value="ci">{t('keisuke.dashboard.chart.errorBarCI')}</option>
              </Form.Select>
            </Form.Group>

            {/* バー幅 */}
            <Form.Group controlId={fid('plot-barWidth')}>
              <Form.Label>{t('keisuke.anova.barWidth')}</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                name={fid('plot-barWidth')}
                min={4}
                max={160}
                step={1}
                value={chartSettings.plot.barWidth}
                onChange={(e) =>
                  setChartSettings((s) => ({ ...s, plot: { ...s.plot, barWidth: parseFloat(e.target.value) || 40 } }))
                }
              />
            </Form.Group>

            {/* バー中心 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.12rem' }}>
              <Form.Label style={{ fontSize: '0.7rem', marginBottom: '0.1rem', color: '#495057' }}>
                {t('keisuke.anova.barCenter')}
              </Form.Label>
              <Form.Check
                type="radio"
                name={fid('barCenter')}
                id={fid('barCenter-mean')}
                label={t('keisuke.anova.barCenterMean')}
                checked={chartSettings.groupDisplay.barCenter === 'mean'}
                disabled={categoryPlotKind === 'box'}
                onChange={() =>
                  setChartSettings((s) => ({ ...s, groupDisplay: { ...s.groupDisplay, barCenter: 'mean' } }))
                }
              />
              <Form.Check
                type="radio"
                name={fid('barCenter')}
                id={fid('barCenter-median')}
                label={t('keisuke.anova.barCenterMedian')}
                checked={chartSettings.groupDisplay.barCenter === 'median'}
                disabled={categoryPlotKind === 'box'}
                onChange={() =>
                  setChartSettings((s) => ({ ...s, groupDisplay: { ...s.groupDisplay, barCenter: 'median' } }))
                }
              />
            </div>

            {/* バー間隔 */}
            <Form.Group className={styles.settingsFieldRange}>
              <Form.Label htmlFor={fid('plot-barSpacing')}>
                {t('keisuke.anova.barSpacing')}
              </Form.Label>
              <Form.Range
                id={fid('plot-barSpacing')}
                name={fid('plot-barSpacing')}
                min={0}
                max={60}
                step={1}
                value={Math.round(chartSettings.plot.barSpacing * 100)}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    plot: { ...s.plot, barSpacing: (parseInt(e.target.value, 10) || 0) / 100 },
                  }))
                }
              />
            </Form.Group>

            {/* X軸ラベル */}
            <Form.Group controlId={fid('xAxis-label')}>
              <Form.Label>{t('keisuke.anova.xAxisLabel')}</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                name={fid('xAxis-label')}
                value={chartSettings.xAxis.label}
                onChange={(e) =>
                  setChartSettings((s) => ({ ...s, xAxis: { ...s.xAxis, label: e.target.value } }))
                }
              />
            </Form.Group>

            {/* Y軸ラベル */}
            <Form.Group controlId={fid('yAxis-label')}>
              <Form.Label>{t('keisuke.anova.yAxisLabel')}</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                name={fid('yAxis-label')}
                value={chartSettings.yAxis.label}
                onChange={(e) =>
                  setChartSettings((s) => ({ ...s, yAxis: { ...s.yAxis, label: e.target.value } }))
                }
              />
            </Form.Group>

            {/* Y軸スケール */}
            <Form.Group controlId={fid('yAxis-scaleType')}>
              <Form.Label>{t('keisuke.anova.yAxisScale')}</Form.Label>
              <Form.Select
                size="sm"
                name={fid('yAxis-scaleType')}
                value={chartSettings.yAxis.scaleType}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    yAxis: {
                      ...s.yAxis,
                      scaleType: normalizeDashboardBarChartYAxisScaleType(e.target.value, s.yAxis.scaleType),
                    },
                  }))
                }
              >
                <option value="linear">{t('keisuke.anova.linear')}</option>
                <option value="log">{t('keisuke.anova.log')}</option>
                <option value="sqrt">{t('keisuke.anova.sqrt')}</option>
                <option value="reciprocal">{t('keisuke.anova.reciprocal')}</option>
              </Form.Select>
            </Form.Group>

            {/* 凡例 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.12rem' }}>
              <Form.Label style={{ fontSize: '0.7rem', marginBottom: '0.1rem', color: '#495057' }}>
                {t('keisuke.anova.legendShow')}
              </Form.Label>
              <Form.Check
                type="checkbox"
                id={fid('legend-show')}
                name={fid('legend-show')}
                label={t('keisuke.anova.legendShow')}
                checked={chartSettings.legend.show}
                onChange={(e) =>
                  setChartSettings((s) => ({ ...s, legend: { ...s.legend, show: e.target.checked } }))
                }
              />
            </div>

            {/* 有意差表示 toggle */}
            <Form.Check
              type="checkbox"
              id={fid('significance-show-basic')}
              name={fid('significance-show-basic')}
              label={t('keisuke.anova.significanceShow')}
              checked={chartSettings.significance.show}
              onChange={(e) =>
                setChartSettings((s) => ({ ...s, significance: { ...s.significance, show: e.target.checked } }))
              }
            />
          </div>
        )}

        {/* ── Y軸タブ ── */}
        {activeTab === 'yaxis' && (
          <div className={styles.settingsFieldGrid}>
            <Form.Group controlId={fid('yAxis-min')}>
              <Form.Label>{t('keisuke.anova.yAxisMin')}</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                name={fid('yAxis-min')}
                step={0.1}
                placeholder={t('keisuke.anova.auto')}
                value={chartSettings.yAxis.minValue ?? ''}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    yAxis: { ...s.yAxis, minValue: e.target.value ? parseFloat(e.target.value) : null },
                  }))
                }
              />
            </Form.Group>

            <Form.Group controlId={fid('yAxis-max')}>
              <Form.Label>{t('keisuke.anova.yAxisMax')}</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                name={fid('yAxis-max')}
                step={0.1}
                placeholder={t('keisuke.anova.auto')}
                value={chartSettings.yAxis.maxValue ?? ''}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    yAxis: { ...s.yAxis, maxValue: e.target.value ? parseFloat(e.target.value) : null },
                  }))
                }
              />
            </Form.Group>

            <Form.Group controlId={fid('yAxis-tickInterval')}>
              <Form.Label>{t('keisuke.anova.yAxisTickInterval')}</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                name={fid('yAxis-tickInterval')}
                min={0}
                step={0.1}
                placeholder={t('keisuke.anova.auto')}
                value={chartSettings.yAxis.tickInterval ?? ''}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    yAxis: { ...s.yAxis, tickInterval: e.target.value ? parseFloat(e.target.value) : null },
                  }))
                }
              />
            </Form.Group>

            <Form.Group className={styles.settingsFieldRange}>
              <Form.Label htmlFor={fid('yAxis-lengthPct')}>
                {t('keisuke.dashboard.chart.yAxisLengthPercent')}：
                {Math.min(100, Math.max(30, chartSettings.yAxis.lengthPercent ?? 100))}%
              </Form.Label>
              <Form.Range
                id={fid('yAxis-lengthPct')}
                name={fid('yAxis-lengthPct')}
                min={30}
                max={100}
                step={5}
                value={Math.min(100, Math.max(30, chartSettings.yAxis.lengthPercent ?? 100))}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    yAxis: { ...s.yAxis, lengthPercent: parseInt(e.target.value, 10) || 100 },
                  }))
                }
              />
            </Form.Group>

            <Form.Group controlId={fid('yAxis-labelPosition')}>
              <Form.Label>{t('keisuke.anova.yAxisLabelPosition')}</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                name={fid('yAxis-labelPosition')}
                step={5}
                value={chartSettings.yAxis.labelPosition}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    yAxis: { ...s.yAxis, labelPosition: parseFloat(e.target.value) || -65 },
                  }))
                }
              />
            </Form.Group>

            <Form.Group controlId={fid('yAxis-lineWidth')}>
              <Form.Label>{t('keisuke.anova.yAxisLineWidth')}</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                name={fid('yAxis-lineWidth')}
                min={1}
                max={5}
                step={0.5}
                value={chartSettings.yAxis.lineWidth}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    yAxis: { ...s.yAxis, lineWidth: parseFloat(e.target.value) || 3 },
                  }))
                }
              />
            </Form.Group>

            <Form.Group controlId={fid('yAxis-tickLabelFormat')}>
              <Form.Label>{t('keisuke.anova.tickLabelFormat')}</Form.Label>
              <Form.Select
                size="sm"
                name={fid('yAxis-tickLabelFormat')}
                value={chartSettings.yAxis.tickLabelFormat}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    yAxis: { ...s.yAxis, tickLabelFormat: e.target.value as 'auto' | 'exponential' },
                  }))
                }
              >
                <option value="auto">{t('keisuke.anova.tickLabelFormatAuto')}</option>
                <option value="exponential">{t('keisuke.anova.tickLabelFormatExp')}</option>
              </Form.Select>
            </Form.Group>

            <Form.Group controlId={fid('yAxis-tickLabelDigits')}>
              <Form.Label>{t('keisuke.anova.tickLabelDigits')}</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                name={fid('yAxis-tickLabelDigits')}
                min={0}
                max={8}
                placeholder="—"
                value={chartSettings.yAxis.tickLabelDigits ?? ''}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    yAxis: {
                      ...s.yAxis,
                      tickLabelDigits: e.target.value === '' ? null : parseInt(e.target.value, 10),
                    },
                  }))
                }
              />
            </Form.Group>

            <Form.Group controlId={fid('yAxis-intersection')}>
              <Form.Label>{t('keisuke.anova.yAxisIntersection')}</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                name={fid('yAxis-intersection')}
                step={0.1}
                placeholder={t('keisuke.anova.auto')}
                value={chartSettings.yAxis.intersectionValue ?? ''}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    yAxis: {
                      ...s.yAxis,
                      intersectionValue: e.target.value === '' ? null : parseFloat(e.target.value),
                    },
                  }))
                }
              />
            </Form.Group>

            {/* グリッド */}
            <div className={styles.settingsSectionDivider}>{t('keisuke.dashboard.chartSettings.sections.grid')}</div>

            <Form.Check
              type="checkbox"
              id={fid('yAxis-gridShow')}
              name={fid('yAxis-gridShow')}
              label={t('keisuke.anova.yAxisGrid')}
              checked={chartSettings.yAxis.grid.show}
              onChange={(e) =>
                setChartSettings((s) => ({
                  ...s,
                  yAxis: { ...s.yAxis, grid: { ...s.yAxis.grid, show: e.target.checked } },
                }))
              }
            />

            <Form.Group controlId={fid('yAxis-gridLineStyle')}>
              <Form.Label>{t('keisuke.anova.gridStyle')}</Form.Label>
              <Form.Select
                size="sm"
                name={fid('yAxis-gridLineStyle')}
                value={chartSettings.yAxis.grid.lineStyle}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    yAxis: {
                      ...s.yAxis,
                      grid: { ...s.yAxis.grid, lineStyle: e.target.value as 'solid' | 'dashed' | 'dotted' },
                    },
                  }))
                }
              >
                <option value="solid">{t('keisuke.dashboard.chart.gridLineSolid')}</option>
                <option value="dashed">{t('keisuke.dashboard.chart.gridLineDashed')}</option>
                <option value="dotted">{t('keisuke.dashboard.chart.gridLineDotted')}</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className={styles.settingsFieldRange}>
              <Form.Label htmlFor={fid('yAxis-gridOpacity')}>
                {t('keisuke.anova.gridOpacity')}
              </Form.Label>
              <Form.Range
                id={fid('yAxis-gridOpacity')}
                name={fid('yAxis-gridOpacity')}
                min={0.1}
                max={1}
                step={0.1}
                value={chartSettings.yAxis.grid.opacity}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    yAxis: { ...s.yAxis, grid: { ...s.yAxis.grid, opacity: parseFloat(e.target.value) } },
                  }))
                }
              />
            </Form.Group>

            <Form.Group controlId={fid('yAxis-gridColor')}>
              <Form.Label>{t('keisuke.anova.gridColor')}</Form.Label>
              <Form.Control
                type="color"
                size="sm"
                name={fid('yAxis-gridColor')}
                value={chartSettings.yAxis.grid.color}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    yAxis: { ...s.yAxis, grid: { ...s.yAxis.grid, color: e.target.value } },
                  }))
                }
              />
            </Form.Group>

            {/* 参照線 */}
            <div className={styles.settingsSectionDivider}>{t('keisuke.dashboard.chartSettings.sections.referenceLine')}</div>

            <Form.Check
              type="checkbox"
              id={fid('refLine-enabled')}
              name={fid('refLine-enabled')}
              label={t('keisuke.dashboard.chart.referenceLineEnabled')}
              checked={chartSettings.referenceLine?.enabled ?? false}
              onChange={(e) =>
                setChartSettings((s) => ({
                  ...s,
                  referenceLine: { ...coalesceReferenceLine(s.referenceLine), enabled: e.target.checked },
                }))
              }
            />

            {(chartSettings.referenceLine?.enabled ?? false) && (
              <Form.Group controlId={fid('refLine-y')}>
                <Form.Label>{t('keisuke.dashboard.chart.referenceLineY')}</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  name={fid('refLine-y')}
                  step="any"
                  placeholder="0"
                  value={
                    chartSettings.referenceLine?.y != null && Number.isFinite(chartSettings.referenceLine.y)
                      ? String(chartSettings.referenceLine.y)
                      : ''
                  }
                  onChange={(e) =>
                    setChartSettings((s) => ({
                      ...s,
                      referenceLine: {
                        ...coalesceReferenceLine(s.referenceLine),
                        y:
                          e.target.value.trim() === ''
                            ? null
                            : Number.isFinite(parseFloat(e.target.value))
                              ? parseFloat(e.target.value)
                              : null,
                      },
                    }))
                  }
                />
              </Form.Group>
            )}
          </div>
        )}

        {/* ── X軸タブ ── */}
        {activeTab === 'xaxis' && (
          <div className={styles.settingsFieldGrid}>
            <Form.Group className={styles.settingsFieldRange}>
              <Form.Label htmlFor={fid('xAxis-lengthPct')}>
                {t('keisuke.anova.xAxisLength')}：{chartSettings.xAxis.lengthPercent}%
              </Form.Label>
              <Form.Range
                id={fid('xAxis-lengthPct')}
                name={fid('xAxis-lengthPct')}
                min={30}
                max={100}
                step={5}
                value={chartSettings.xAxis.lengthPercent}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    xAxis: { ...s.xAxis, lengthPercent: parseInt(e.target.value) || 100 },
                  }))
                }
              />
            </Form.Group>

            <Form.Group controlId={fid('xAxis-tickRotation')}>
              <Form.Label>{t('keisuke.anova.xAxisTickLabelRotation')}</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                name={fid('xAxis-tickRotation')}
                min={-360}
                max={360}
                value={chartSettings.xAxis.tickLabelRotation}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    xAxis: { ...s.xAxis, tickLabelRotation: parseInt(e.target.value, 10) || 0 },
                  }))
                }
              />
            </Form.Group>

            <Form.Group controlId={fid('xAxis-tickAnchor')}>
              <Form.Label>{t('keisuke.anova.xAxisTickLabelAnchor')}</Form.Label>
              <Form.Select
                size="sm"
                name={fid('xAxis-tickAnchor')}
                value={chartSettings.xAxis.tickLabelAnchor}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    xAxis: { ...s.xAxis, tickLabelAnchor: e.target.value as 'start' | 'middle' | 'end' },
                  }))
                }
              >
                <option value="start">{t('keisuke.dashboard.chart.tickLabelAnchorStart')}</option>
                <option value="middle">{t('keisuke.dashboard.chart.tickLabelAnchorMiddle')}</option>
                <option value="end">{t('keisuke.dashboard.chart.tickLabelAnchorEnd')}</option>
              </Form.Select>
            </Form.Group>

            <Form.Group controlId={fid('xAxis-lineWidth')}>
              <Form.Label>{t('keisuke.anova.xAxisLineWidth')}</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                name={fid('xAxis-lineWidth')}
                min={1}
                max={5}
                step={0.5}
                value={chartSettings.xAxis.lineWidth}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    xAxis: { ...s.xAxis, lineWidth: parseFloat(e.target.value) || 3 },
                  }))
                }
              />
            </Form.Group>

            <Form.Group controlId={fid('xAxis-intersection')}>
              <Form.Label>{t('keisuke.anova.xAxisIntersection')}</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                name={fid('xAxis-intersection')}
                step={0.01}
                placeholder={t('keisuke.anova.auto')}
                value={chartSettings.xAxis.intersectionValue ?? ''}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    xAxis: {
                      ...s.xAxis,
                      intersectionValue: e.target.value === '' ? null : parseFloat(e.target.value),
                    },
                  }))
                }
              />
            </Form.Group>

            {/* X軸グリッド */}
            <div className={styles.settingsSectionDivider}>{t('keisuke.dashboard.chartSettings.sections.grid')}</div>

            <Form.Check
              type="checkbox"
              id={fid('xAxis-gridShow')}
              name={fid('xAxis-gridShow')}
              label={t('keisuke.anova.xAxisGrid')}
              checked={chartSettings.xAxis.grid.show}
              onChange={(e) =>
                setChartSettings((s) => ({
                  ...s,
                  xAxis: { ...s.xAxis, grid: { ...s.xAxis.grid, show: e.target.checked } },
                }))
              }
            />

            <Form.Group controlId={fid('xAxis-gridLineStyle')}>
              <Form.Label>{t('keisuke.anova.gridStyle')}</Form.Label>
              <Form.Select
                size="sm"
                name={fid('xAxis-gridLineStyle')}
                value={chartSettings.xAxis.grid.lineStyle}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    xAxis: {
                      ...s.xAxis,
                      grid: { ...s.xAxis.grid, lineStyle: e.target.value as 'solid' | 'dashed' | 'dotted' },
                    },
                  }))
                }
              >
                <option value="solid">{t('keisuke.dashboard.chart.gridLineSolid')}</option>
                <option value="dashed">{t('keisuke.dashboard.chart.gridLineDashed')}</option>
                <option value="dotted">{t('keisuke.dashboard.chart.gridLineDotted')}</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className={styles.settingsFieldRange}>
              <Form.Label htmlFor={fid('xAxis-gridOpacity')}>
                {t('keisuke.anova.gridOpacity')}
              </Form.Label>
              <Form.Range
                id={fid('xAxis-gridOpacity')}
                name={fid('xAxis-gridOpacity')}
                min={0.1}
                max={1}
                step={0.1}
                value={chartSettings.xAxis.grid.opacity}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    xAxis: { ...s.xAxis, grid: { ...s.xAxis.grid, opacity: parseFloat(e.target.value) } },
                  }))
                }
              />
            </Form.Group>

            <Form.Group controlId={fid('xAxis-gridColor')}>
              <Form.Label>{t('keisuke.anova.gridColor')}</Form.Label>
              <Form.Control
                type="color"
                size="sm"
                name={fid('xAxis-gridColor')}
                value={chartSettings.xAxis.grid.color}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    xAxis: { ...s.xAxis, grid: { ...s.xAxis.grid, color: e.target.value } },
                  }))
                }
              />
            </Form.Group>

            {/* 垂直参照線 */}
            <div className={styles.settingsSectionDivider}>{t('keisuke.dashboard.chartSettings.sections.verticalReferenceLine')}</div>

            <Form.Check
              type="checkbox"
              id={fid('refLine-v-enabled')}
              name={fid('refLine-v-enabled')}
              label={t('keisuke.dashboard.chart.referenceLineVerticalEnabled')}
              checked={chartSettings.referenceLine?.verticalEnabled ?? false}
              disabled={categories.length === 0}
              onChange={(e) =>
                setChartSettings((s) => ({
                  ...s,
                  referenceLine: {
                    ...coalesceReferenceLine(s.referenceLine),
                    verticalEnabled: e.target.checked,
                  },
                }))
              }
            />

            {(chartSettings.referenceLine?.verticalEnabled ?? false) && categories.length > 0 && (
              <Form.Group controlId={fid('refLine-v-category')} className={styles.settingsFieldFull}>
                <Form.Label>{t('keisuke.dashboard.chart.referenceLineVerticalCategory')}</Form.Label>
                <Form.Select
                  size="sm"
                  name={fid('refLine-v-category')}
                  value={chartSettings.referenceLine?.verticalCategory ?? ''}
                  onChange={(e) =>
                    setChartSettings((s) => ({
                      ...s,
                      referenceLine: {
                        ...coalesceReferenceLine(s.referenceLine),
                        verticalCategory: e.target.value === '' ? null : e.target.value,
                      },
                    }))
                  }
                >
                  <option value="">{t('keisuke.dashboard.chart.referenceLineVerticalPlaceholder')}</option>
                  {categories.map((cat) => (
                    <option key={`vref-${cat}`} value={cat}>
                      {cat}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
          </div>
        )}

        {/* ── 有意差タブ ── */}
        {activeTab === 'significance' && (
          <div className={styles.settingsFieldGrid}>
            <Form.Check
              type="checkbox"
              id={fid('significance-show')}
              name={fid('significance-show')}
              label={t('keisuke.anova.significanceShow')}
              checked={chartSettings.significance.show}
              onChange={(e) =>
                setChartSettings((s) => ({ ...s, significance: { ...s.significance, show: e.target.checked } }))
              }
            />

            <Form.Group controlId={fid('significance-style')}>
              <Form.Label>{t('keisuke.anova.significanceStyle')}</Form.Label>
              <Form.Select
                size="sm"
                name={fid('significance-style')}
                value={chartSettings.significance.style}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    significance: { ...s.significance, style: e.target.value as 'p' | 'asterisk' },
                  }))
                }
              >
                <option value="p">{t('keisuke.anova.significanceP')}</option>
                <option value="asterisk">{t('keisuke.anova.significanceAsterisk')}</option>
              </Form.Select>
            </Form.Group>

            <Form.Group controlId={fid('significance-displayMode')}>
              <Form.Label>{t('keisuke.anova.significanceScope')}</Form.Label>
              <Form.Select
                size="sm"
                name={fid('significance-displayMode')}
                value={chartSettings.significance.displayMode}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    significance: { ...s.significance, displayMode: e.target.value as 'all' | 'topN' },
                  }))
                }
              >
                <option value="all">{t('keisuke.anova.significanceScopeAll')}</option>
                <option value="topN">{t('keisuke.anova.significanceScopeTopN')}</option>
              </Form.Select>
            </Form.Group>

            {chartSettings.significance.displayMode === 'topN' && (
              <Form.Group controlId={fid('significance-topN')}>
                <Form.Label>{t('keisuke.anova.significanceTopN')}</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  name={fid('significance-topN')}
                  min={1}
                  max={50}
                  value={chartSettings.significance.topN}
                  onChange={(e) =>
                    setChartSettings((s) => ({
                      ...s,
                      significance: { ...s.significance, topN: parseInt(e.target.value, 10) || 1 },
                    }))
                  }
                />
              </Form.Group>
            )}

            <Form.Group className={styles.settingsFieldRange}>
              <Form.Label htmlFor={fid('significance-offset')}>
                {t('keisuke.anova.significanceOffset')}：{chartSettings.significance.offset}px
              </Form.Label>
              <Form.Range
                id={fid('significance-offset')}
                name={fid('significance-offset')}
                min={16}
                max={200}
                step={1}
                value={chartSettings.significance.offset}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    significance: { ...s.significance, offset: parseInt(e.target.value, 10) || 50 },
                  }))
                }
              />
            </Form.Group>

            <Form.Group className={styles.settingsFieldRange}>
              <Form.Label htmlFor={fid('significance-gap')}>
                {t('keisuke.anova.significanceGap')}：{chartSettings.significance.gap}px
              </Form.Label>
              <Form.Range
                id={fid('significance-gap')}
                name={fid('significance-gap')}
                min={4}
                max={80}
                step={1}
                value={chartSettings.significance.gap}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    significance: { ...s.significance, gap: parseInt(e.target.value, 10) || 16 },
                  }))
                }
              />
            </Form.Group>

            {/* 凡例位置 */}
            <div className={styles.settingsSectionDivider}>{t('keisuke.dashboard.chartSettings.sections.legend')}</div>

            <Form.Check
              type="checkbox"
              id={fid('legend-show-sig')}
              name={fid('legend-show-sig')}
              label={t('keisuke.anova.legendShow')}
              checked={chartSettings.legend.show}
              onChange={(e) =>
                setChartSettings((s) => ({ ...s, legend: { ...s.legend, show: e.target.checked } }))
              }
            />

            <Form.Group controlId={fid('legend-position')} className={styles.settingsFieldFull}>
              <Form.Label>{t('keisuke.anova.legendPosition')}</Form.Label>
              <Form.Select
                size="sm"
                id={fid('legend-position')}
                name={fid('legend-position')}
                value={chartSettings.legend.position}
                onChange={(e) =>
                  setChartSettings((s) => ({
                    ...s,
                    legend: { ...s.legend, position: e.target.value as 'top' | 'bottom' | 'right' | 'free' },
                  }))
                }
              >
                <option value="top">{t('keisuke.dashboard.chart.legendPositionTop')}</option>
                <option value="bottom">{t('keisuke.dashboard.chart.legendPositionBottom')}</option>
                <option value="right">{t('keisuke.dashboard.chart.legendPositionRight')}</option>
                <option value="free">{t('keisuke.dashboard.chart.legendPositionFree')}</option>
              </Form.Select>
            </Form.Group>

            {chartSettings.legend.position === 'free' && (
              <>
                <Form.Group className={styles.settingsFieldRange}>
                  <Form.Label htmlFor={fid('legend-freeOffsetX')}>
                    {t('keisuke.dashboard.chart.legendFreeOffsetX')}：
                    {Math.round(chartSettings.legend.freeOffsetXPct)}%
                  </Form.Label>
                  <Form.Range
                    id={fid('legend-freeOffsetX')}
                    name={fid('legend-freeOffsetX')}
                    min={0}
                    max={100}
                    value={chartSettings.legend.freeOffsetXPct}
                    onChange={(e) =>
                      setChartSettings((s) => ({
                        ...s,
                        legend: { ...s.legend, freeOffsetXPct: Number(e.target.value) },
                      }))
                    }
                  />
                </Form.Group>
                <Form.Group className={styles.settingsFieldRange}>
                  <Form.Label htmlFor={fid('legend-freeOffsetY')}>
                    {t('keisuke.dashboard.chart.legendFreeOffsetY')}：
                    {Math.round(chartSettings.legend.freeOffsetYPct)}%
                  </Form.Label>
                  <Form.Range
                    id={fid('legend-freeOffsetY')}
                    name={fid('legend-freeOffsetY')}
                    min={0}
                    max={100}
                    value={chartSettings.legend.freeOffsetYPct}
                    onChange={(e) =>
                      setChartSettings((s) => ({
                        ...s,
                        legend: { ...s.legend, freeOffsetYPct: Number(e.target.value) },
                      }))
                    }
                  />
                </Form.Group>
              </>
            )}
          </div>
        )}

        {/* ── 詳細タブ ── */}
        {activeTab === 'detail' && (
          <>
            {/* フォント */}
            <div className={styles.settingsSectionDivider} style={{ marginTop: 0 }}>{t('keisuke.dashboard.chartSettings.sections.font')}</div>
            <KeisukeUnifiedChartFontControls
              t={t}
              chartSettings={chartSettings}
              setChartSettings={setChartSettings}
              fieldsRowClassName={styles.settingsFieldGrid}
              headingClassName={undefined}
              headingIconClass={undefined}
              headingIconWrapperClassName={undefined}
              fieldIdPrefix={`${fieldIdPrefix}-font`}
            />

            {/* サイズ */}
            <div className={styles.settingsSectionDivider}>{t('keisuke.dashboard.chartSettings.sections.size')}</div>
            <div className={styles.settingsFieldGrid}>
              <Form.Group controlId={fid('chart-width')}>
                <Form.Label>{t('keisuke.dashboard.chart.width')}</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  name={fid('chart-width')}
                  min={400}
                  step={1}
                  placeholder={t('keisuke.dashboard.chart.auto')}
                  value={chartSettings.size.width ?? ''}
                  onChange={(e) =>
                    setChartSettings((s) => ({
                      ...s,
                      size: { ...s.size, width: e.target.value ? parseInt(e.target.value, 10) : null },
                    }))
                  }
                />
              </Form.Group>

              <Form.Group controlId={fid('chart-height')}>
                <Form.Label>{t('keisuke.dashboard.chart.height')}</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  name={fid('chart-height')}
                  min={320}
                  step={1}
                  placeholder={t('keisuke.dashboard.chart.auto')}
                  value={chartSettings.size.height ?? ''}
                  onChange={(e) =>
                    setChartSettings((s) => ({
                      ...s,
                      size: { ...s.size, height: e.target.value ? parseInt(e.target.value, 10) : null },
                    }))
                  }
                />
              </Form.Group>

              <Form.Group controlId={fid('chart-plotAreaWidth')}>
                <Form.Label>{t('keisuke.anova.plotAreaWidth')}</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  name={fid('chart-plotAreaWidth')}
                  min={200}
                  step={1}
                  placeholder={t('keisuke.dashboard.chart.auto')}
                  value={chartSettings.size.plotAreaWidth ?? ''}
                  onChange={(e) =>
                    setChartSettings((s) => ({
                      ...s,
                      size: { ...s.size, plotAreaWidth: e.target.value ? parseInt(e.target.value, 10) : null },
                    }))
                  }
                />
              </Form.Group>

              <Form.Group controlId={fid('chart-plotAreaHeight')}>
                <Form.Label>{t('keisuke.anova.plotAreaHeight')}</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  name={fid('chart-plotAreaHeight')}
                  min={200}
                  step={1}
                  placeholder={t('keisuke.dashboard.chart.auto')}
                  value={chartSettings.size.plotAreaHeight ?? ''}
                  onChange={(e) =>
                    setChartSettings((s) => ({
                      ...s,
                      size: { ...s.size, plotAreaHeight: e.target.value ? parseInt(e.target.value, 10) : null },
                    }))
                  }
                />
              </Form.Group>

              {/* 背景色 */}
              <div
                className={styles.settingsFieldFull}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}
              >
                <Form.Label style={{ fontSize: '0.7rem', marginBottom: 0, color: '#495057', flexShrink: 0 }}>
                  {t('keisuke.dashboard.chart.backgroundColor')}
                </Form.Label>
                <Form.Check
                  type="checkbox"
                  id={fid('chart-bg-transparent')}
                  name={fid('chart-bg-transparent')}
                  label={t('keisuke.dashboard.chart.backgroundColorTransparent')}
                  checked={chartSettings.chartBackgroundColor === 'transparent'}
                  onChange={(e) =>
                    setChartSettings((s) => ({
                      ...s,
                      chartBackgroundColor: e.target.checked ? 'transparent' : '#ffffff',
                    }))
                  }
                />
                {chartSettings.chartBackgroundColor !== 'transparent' && (
                  <Form.Control
                    id={fid('chart-bg-color')}
                    type="color"
                    size="sm"
                    name={fid('chart-bg-color')}
                    value={chartSettings.chartBackgroundColor}
                    onChange={(e) =>
                      setChartSettings((s) => ({ ...s, chartBackgroundColor: e.target.value }))
                    }
                    style={{ width: 40, height: 28, padding: '0.1rem', borderRadius: '0.25rem', flexShrink: 0 }}
                  />
                )}
              </div>
            </div>

            {/* 余白補正 */}
            <div className={styles.settingsSectionDivider}>{t('keisuke.dashboard.chartSettings.sections.marginAdjust')}</div>
            <div className={styles.settingsMarginGrid}>
              {(['top', 'right', 'bottom', 'left'] as const).map((side) => (
                <div key={side}>
                  <Form.Label>
                    {side === 'top'
                      ? t('keisuke.dashboard.chart.marginAdjust.top')
                      : side === 'right'
                        ? t('keisuke.dashboard.chart.marginAdjust.right')
                        : side === 'bottom'
                          ? t('keisuke.dashboard.chart.marginAdjust.bottom')
                          : t('keisuke.dashboard.chart.marginAdjust.left')}
                  </Form.Label>
                  <InputGroup size="sm">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      aria-label={`-1 ${side}`}
                      onClick={() =>
                        setChartSettings((s) => {
                          const cur = normalizeMarginAdjust(s.marginAdjust);
                          return { ...s, marginAdjust: normalizeMarginAdjust({ ...cur, [side]: cur[side] - 1 }) };
                        })
                      }
                    >
                      −
                    </Button>
                    <Form.Control
                      type="number"
                      min={-200}
                      max={200}
                      step={1}
                      value={normalizeMarginAdjust(chartSettings.marginAdjust)[side]}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setChartSettings((s) => ({
                          ...s,
                          marginAdjust: normalizeMarginAdjust({
                            ...normalizeMarginAdjust(s.marginAdjust),
                            [side]: Number.isFinite(v) ? v : 0,
                          }),
                        }));
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline-secondary"
                      aria-label={`+1 ${side}`}
                      onClick={() =>
                        setChartSettings((s) => {
                          const cur = normalizeMarginAdjust(s.marginAdjust);
                          return { ...s, marginAdjust: normalizeMarginAdjust({ ...cur, [side]: cur[side] + 1 }) };
                        })
                      }
                    >
                      +
                    </Button>
                  </InputGroup>
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="link"
              size="sm"
              style={{ padding: 0, fontSize: '0.72rem', marginTop: '0.25rem' }}
              onClick={() =>
                setChartSettings((s) => ({
                  ...s,
                  marginAdjust: { top: 0, right: 0, bottom: 0, left: 0 },
                }))
              }
            >
              {t('keisuke.dashboard.chart.marginAdjustReset')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
